<template>
  <v-row justify="center">
    <v-dialog v-model="dialogForm" persistent max-width="800px">
      <v-card>
        <v-toolbar color="tertiary" flat>
          <v-toolbar-title class="text-h6">{{ formTitle }}</v-toolbar-title>
          <v-spacer/>
        </v-toolbar>
        <v-form v-model="valid" lazy-validation @submit.prevent="submit" ref="form">
          <v-card-text class="pb-0">
            <v-container fluid>
              <v-row>
                <v-col class="py-0" cols="6">
                  <v-autocomplete
                    label="Estados"
                    v-model="dataSearch.estado"
                    :items="estados"
                    :search-input.sync="searchEstadosAutoComplete"
                    outlined
                    hide-no-data
                    hide-selected
                    item-text="nome"
                    item-value="id"
                    :aria-autocomplete="false"
                    clearable
                    dense
                    :rules="rules.estado"
                    :error-messages="errors.estado"
                    @change="getCidades"
                  >
                  </v-autocomplete>
                </v-col>

                <v-col class="py-0" cols="6">
                  <v-autocomplete
                    label="Cidades"
                    v-model="dataSearch.cidade_id"
                    :items="cidades"
                    :search-input.sync="searchCidadesAutoComplete"
                    outlined
                    hide-no-data
                    hide-selected
                    item-text="nome"
                    item-value="id"
                    :aria-autocomplete="false"
                    clearable
                    dense
                    :rules="rules.cidade_id"
                    :loading="isloadingCidades"
                    :error-messages="errors.cidade_id"
                    @change="loadBairros"
                  >
                  </v-autocomplete>
                </v-col>
              </v-row>
              <v-row>
                <v-col class="py-0" cols="4">
                  <v-text-field
                    label="CEP"
                    :error-messages="errors.cep"
                    v-model="formData.cep"
                    :counter="9"
                    :rules="rules.cep"
                    v-mask="'#####-###'"
                    outlined
                    dense
                    class="pa-0"
                  >
                  </v-text-field>
                </v-col>

                <v-col class="py-0" cols="8">
                  <v-autocomplete
                    label="Tipo Logradouro"
                    v-model="formData.tipo_logradouro_id"
                    :items="tipos_logradouro"
                    :loading="isloadingTipoLogradouro"
                    outlined
                    hide-no-data
                    hide-selected
                    item-text="nome"
                    item-value="id"
                    :aria-autocomplete="false"
                    clearable
                    dense
                    :rules="rules.tipo_logradouro_id"
                    :error-messages="errors.tipo_logradouro_id"
                  >
                  </v-autocomplete>
                </v-col>
              </v-row>

              <v-row>
                <v-col class="py-0" cols="12">
                  <v-text-field
                    label="Endereço *"
                    :error-messages="errors.nome"
                    v-model="formData.nome"
                    :counter="255"
                    :rules="rules.nome"
                    outlined
                    dense
                    class="pa-0"
                  >
                  </v-text-field>
                </v-col>
              </v-row>

              <v-row>
                <v-col class="py-0" cols="12">
                  <v-autocomplete
                    :error-messages="errors.bairros"
                    :items="bairros"
                    :loading="isloadingBairro"
                    :rules="rules.bairros"
                    chips
                    small-chips
                    clearable
                    dense
                    hide-no-data
                    hide-selected
                    item-text="nome"
                    item-value="id"
                    label="Bairros"
                    multiple
                    outlined
                    persistent-hint
                    placeholder="Selecione o bairro desejado"
                    v-model="formData.bairros"
                  >
                    <template v-slot:selection="data">
                      <v-chip
                        :input-value="data.selected"
                        @click="data.select"
                        @click:close="removeBairro(data.item)"
                        close
                        v-bind="data.attrs"
                      >
                        {{ data.item.nome }}
                      </v-chip>
                    </template>

                    <template v-slot:item="data">
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ data.item.nome }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>

              <v-row>
                <v-col class="py-0" cols="12">
                  <v-textarea
                    label="Observação"
                    :error-messages="errors.observacao"
                    v-model="formData.observacao"
                    :counter="500"
                    :rules="rules.observacao"
                    outlined
                    dense
                    class="pa-0"
                    rows="3"
                  >
                  </v-textarea>
                </v-col>
              </v-row>

              <v-row>
                <v-col class="py-0" cols="12">
                  <v-switch label="Ativo?" v-model="formData.ativo"></v-switch>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn color="red" dark
                   depressed
                   @click="toggleForm">
              <v-icon small left>mdi-close</v-icon>
              Fechar
            </v-btn>
            <v-btn
              :disabled="!valid"
              depressed
              :loading="isloadingSubmit"
              color="green" dark
              type="submit">
              <v-icon small left>mdi-send</v-icon>
              Salvar
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { max, required } from '@/services/util/validate';
import bairroHttp from '@/services/http/resources/configuracao/bairroHttp';
import tipoLogradouroHttp from '@/services/http/resources/configuracao/tipoLogradouroHttp';
import toastMessage from '@/services/util/alertService';
import cidadeHttp from '@/services/http/resources/configuracao/cidadeHttp';
import { mask } from 'vue-the-mask';

export default {
  name: 'LogradouroModalForm',
  directives: {
    mask,
  },
  data: () => ({
    valid: true,
    cidades: [],
    bairros: [],
    tipos_logradouro: [],
    searchEstadosAutoComplete: '',
    searchCidadesAutoComplete: '',
    searchTiposLogradouroAutoComplete: '',
    dataSearch: {
      estado: '',
      cidade_id: '',
    },
    formData: {
      id: '',
      nome: '',
      cep: '',
      observacao: '',
      ativo: false,
      tipo_logradouro_id: '',
      bairros: [],
    },
    formDataDefault: {
      id: '',
      nome: '',
      cep: '',
      observacao: '',
      ativo: false,
      tipo_logradouro_id: '',
      bairros: [],
    },
    rules: {
      nome: [
        required,
        (value) => max(value, 255),
      ],
      cep: [
        (value) => max(value, 9),
      ],
      observacao: [
        (value) => max(value, 500),
      ],
      tipo_logradouro_id: [required],
    },
    errors: {
      nome: [],
      cep: [],
      observacao: [],
      tipo_logradouro_id: [],
    },
    isloadingSubmit: false,
    isloadingBairro: false,
    isloadingCidades: false,
    isloadingTipoLogradouro: false,
  }),
  computed: {
    ...mapState('estados', { estados: 'listPluck' }),
    ...mapState('configuracao', ['config']),
    ...mapState('logradouro', ['dataIndex', 'data', 'dialogForm']),
    formTitle() {
      return this.dataIndex === -1 ? 'Novo Logradouro' : 'Editar Logradouro';
    },
    disabledInput() {
      return !(this.dataIndex === -1);
    },
  },
  created() {
    this.getEstados();
  },
  methods: {
    ...mapActions('estados', ['list']),
    ...mapActions('logradouro', [
      'toggleForm',
      'store',
      'update',
      'setDataIndex',
      'clearData',
    ]),
    resetValidation() {
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    submit() {
      if (this.$refs.form.validate()) {
        this.isloadingSubmit = true;
        const actionHttp = (this.dataIndex === -1)
          ? this.store(this.formData) : this.update(this.formData);
        actionHttp.then(() => {
          this.$bus.$emit('checklogradouro');
          toastMessage.successSave('Logradouro salvo com sucesso');
          this.resetValidation();
        })
          .catch(({ response }) => {
            if (response.status === 422) {
              const { errors } = response.data;
              Object.entries(errors)
                .forEach(([index, value]) => {
                  this.errors[index].push(value.toString());
                  toastMessage.errorValidation();
                });
            } else {
              toastMessage.errorSave('Erro ao salvar logradouro');
            }
          })
          .then(() => {
            this.isloadingSubmit = false;
          });
      }
    },
    openDialogForm() {
      this.resetValidation();
      this.setEstadoAndCidade();
      this.loadBairros();
      this.loadTiposLogradouro();
      if (this.dataIndex > -1) {
        Object.entries(this.formDataDefault)
          .forEach(([key]) => {
            if (key === 'bairros' && this.data[key]) {
              this.formData[key] = this.data[key].map((item) => item.id);
            } else {
              this.formData[key] = this.data[key];
            }
          });
      }
    },
    closeDialogForm() {
      setTimeout(() => {
        Object.entries(this.formDataDefault)
          .forEach(([key]) => {
            this.formData[key] = this.formDataDefault[key];
          });
        this.resetValidation();
        this.resetTipoLogradouroAutoComplete();
        this.clearData();
        this.setDataIndex(-1);
      }, 300);
    },
    getEstados() {
      if (!this.estados.length) {
        this.list();
      }
    },
    getCidades() {
      this.resetCidadesAutoComplete();
      this.resetBairrosAutoComplete();
      if (!this.dataSearch.estado) return;
      this.isloadingCidades = true;
      cidadeHttp.listCidades(this.dataSearch.estado)
        .then(({ data: { data } }) => {
          this.cidades = data;
        })
        .catch(() => toastMessage.errorLoading('Erro ao carregar cidades'))
        .finally(() => {
          this.isloadingCidades = false;
        });
    },
    loadBairros() {
      this.resetBairrosAutoComplete();
      if (!this.dataSearch.cidade_id) return;
      this.isloadingBairro = true;
      bairroHttp.listBairroCidade(this.dataSearch.cidade_id)
        .then(({ data: { data } }) => {
          this.bairros = data;
        })
        .finally(() => {
          this.isloadingBairro = false;
        });
    },
    loadTiposLogradouro() {
      this.isloadingTipoLogradouro = true;
      tipoLogradouroHttp.list()
        .then(({ data: { data } }) => {
          this.tipos_logradouro = data;
        })
        .finally(() => {
          this.isloadingTipoLogradouro = false;
        });
    },
    removeBairro(item) {
      const index = this.formData.bairros.indexOf(item.id);
      if (index >= 0) this.formData.bairros.splice(index, 1);
    },
    resetBairrosAutoComplete() {
      this.bairros = [];
      this.formData.bairros = [];
    },
    resetTipoLogradouroAutoComplete() {
      this.formData.tipo_logradouro_id = '';
      this.searchTiposLogradouroAutoComplete = null;
    },
    resetEstadosAutoComplete() {
      this.dataSearch.estado = '';
      this.searchEstadosAutoComplete = null;
    },
    resetCidadesAutoComplete() {
      this.cidades = [];
      this.dataSearch.cidade_id = '';
      this.searchCidadesAutoComplete = null;
    },
    setEstadoAndCidade() {
      Object.entries(this.dataSearch)
        .forEach(([key]) => {
          if (key === 'estado') {
            if (Object.keys(this.data).length) {
              if (this.data.bairros.length) {
                this.dataSearch[key] = this.data.bairros[0].cidade.estado_id;
              } else {
                this.dataSearch[key] = this.config.estado_id;
              }
            } else {
              this.dataSearch[key] = this.config.estado_id;
            }
            this.getCidades();
          } else if (key === 'cidade_id') {
            if (Object.keys(this.data).length) {
              if (this.data.bairros.length) {
                this.dataSearch[key] = this.data.bairros[0].cidade_id;
              } else {
                this.dataSearch[key] = this.config.cidade_id;
              }
            } else {
              this.dataSearch[key] = this.config.cidade_id;
            }
          }
        });
    },
  },
  watch: {
    dialogForm(value) {
      if (value) {
        this.openDialogForm();
      } else {
        this.closeDialogForm();
      }
    },
    'formData.nome': function () {
      this.errors.nome = [];
    },
    'formData.cep': function () {
      this.errors.cep = [];
    },
    'formData.observacao': function () {
      this.errors.observacao = [];
    },
    'formData.tipo_logradouro_id': function () {
      this.errors.tipo_logradouro_id = [];
    },
  },
};
</script>

<style></style>
